import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface CheckinRow {
  id: string;
  checkin_date: string;
  checkin_at: string;
  checkout_at: string | null;
  work_log: string | null;
  mood: number;
}

const MOOD_LIST = [
  { value: 1, emoji: "😫", label: "힘들어요" },
  { value: 2, emoji: "😕", label: "좀 지침" },
  { value: 3, emoji: "🙂", label: "보통" },
  { value: 4, emoji: "😊", label: "좋아요" },
  { value: 5, emoji: "🔥", label: "최고예요" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function CheckInPanel() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();

  const [todayRecord, setTodayRecord] = useState<CheckinRow | null>(null);
  const [history, setHistory] = useState<CheckinRow[]>([]);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [workLog, setWorkLog] = useState("");
  const [mood, setMood] = useState(3);
  const [showLogInput, setShowLogInput] = useState(false);
  const [logSaved, setLogSaved] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user || !workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("daily_checkins")
      .select("*")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id)
      .order("checkin_date", { ascending: false })
      .limit(30);

    const records = (data ?? []) as CheckinRow[];
    setHistory(records);

    const today = records.find(r => r.checkin_date === todayStr());
    setTodayRecord(today ?? null);
    if (today) { setWorkLog(today.work_log ?? ""); setMood(today.mood ?? 3); }

    // 연속 출근 계산
    let s = 0;
    const dateSet = new Set(records.map(r => r.checkin_date));
    const cur = new Date();
    // 오늘 출근했으면 오늘부터, 안 했으면 어제부터
    if (!dateSet.has(todayStr())) cur.setDate(cur.getDate() - 1);
    while (dateSet.has(cur.toISOString().slice(0, 10))) {
      s++; cur.setDate(cur.getDate() - 1);
    }
    setStreak(s);
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCheckIn = async () => {
    if (!user || !workspaceId || saving) return;
    setSaving(true);
    const { data } = await supabase.from("daily_checkins").insert({
      workspace_id: workspaceId,
      user_id: user.id,
      checkin_date: todayStr(),
      mood,
    }).select().maybeSingle();
    setSaving(false);
    if (data) { setTodayRecord(data as CheckinRow); setStreak(s => s + 1); setShowLogInput(true); }
  };

  const handleCheckOut = async () => {
    if (!todayRecord || saving) return;
    setSaving(true);
    await supabase.from("daily_checkins").update({ checkout_at: new Date().toISOString() }).eq("id", todayRecord.id);
    setSaving(false);
    setTodayRecord(prev => prev ? { ...prev, checkout_at: new Date().toISOString() } : null);
  };

  const handleSaveLog = async () => {
    if (!todayRecord || saving) return;
    setSaving(true);
    await supabase.from("daily_checkins").update({ work_log: workLog.trim() || null, mood }).eq("id", todayRecord.id);
    setSaving(false);
    setTodayRecord(prev => prev ? { ...prev, work_log: workLog.trim() || null, mood } : null);
    setShowLogInput(false);
    setLogSaved(true);
    setTimeout(() => setLogSaved(false), 3000);
  };

  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });
  const checkedDates = new Set(history.map(r => r.checkin_date));

  if (loading) return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-4 animate-pulse h-28" />
  );

  const checkedIn = !!todayRecord;
  const checkedOut = !!todayRecord?.checkout_at;
  const now = new Date();
  const checkinTime = todayRecord ? new Date(todayRecord.checkin_at) : null;
  const workHours = checkinTime && todayRecord?.checkout_at
    ? Math.round((new Date(todayRecord.checkout_at).getTime() - checkinTime.getTime()) / 36000) / 100
    : checkinTime
    ? Math.round((now.getTime() - checkinTime.getTime()) / 36000) / 100
    : 0;

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      <button type="button" onClick={() => setCollapsed(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${checkedIn ? "bg-[#00C896]/15" : "bg-white/[0.04]"}`}>
            <i className={`ri-time-line text-base ${checkedIn ? "text-[#00C896]" : "text-[#444444]"}`}></i>
          </div>
          <div className="text-left">
            <div className="text-[#F2F2F2] text-sm font-semibold flex items-center gap-2">
              출근 체크인
              {checkedIn && !checkedOut && (
                <span className="flex items-center gap-1 text-[9px] font-bold text-[#00C896] bg-[#00C896]/10 border border-[#00C896]/25 px-1.5 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00C896] animate-pulse inline-block"></span>근무 중
                </span>
              )}
              {checkedOut && (
                <span className="text-[9px] font-bold text-[#555555] bg-white/[0.03] border border-white/[0.07] px-1.5 py-0.5 rounded-full">퇴근 완료</span>
              )}
            </div>
            <div className="text-[#444444] text-[10px]">
              {streak > 0 ? `🔥 ${streak}일 연속 출근 중` : "오늘 첫 출근을 기록하세요"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {checkedIn && <span className="text-[#555555] text-xs tabular-nums">{workHours.toFixed(1)}h</span>}
          <i className={`ri-arrow-${collapsed ? "down" : "up"}-s-line text-[#555555] text-sm`}></i>
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 py-4">
          {/* 최근 7일 출근 현황 */}
          <div className="flex items-center gap-1.5 mb-4">
            {last7.map(d => {
              const isToday = d === todayStr();
              const checked = checkedDates.has(d);
              const rec = history.find(r => r.checkin_date === d);
              const moodEmoji = rec ? MOOD_LIST.find(m => m.value === rec.mood)?.emoji : null;
              return (
                <div key={d} className="flex-1 flex flex-col items-center gap-1" title={d}>
                  <div className={`w-full aspect-square rounded-lg flex items-center justify-center text-sm transition-all
                    ${checked ? "bg-[#00C896]/15 border border-[#00C896]/30" : isToday ? "border border-dashed border-[#444444]" : "bg-white/[0.02] border border-white/[0.04]"}`}>
                    {checked ? (moodEmoji ?? <i className="ri-check-line text-[#00C896] text-xs"></i>) : isToday ? <i className="ri-circle-line text-[#444444] text-xs"></i> : null}
                  </div>
                  <span className={`text-[8px] ${isToday ? "text-[#00C896]" : "text-[#333333]"}`}>
                    {["일","월","화","수","목","금","토"][new Date(d + "T12:00:00").getDay()]}
                  </span>
                </div>
              );
            })}
          </div>

          {!checkedIn ? (
            /* 미출근 상태 */
            <div className="space-y-3">
              <div>
                <div className="text-[#444444] text-[10px] mb-2">오늘 컨디션은 어때요?</div>
                <div className="flex gap-1.5">
                  {MOOD_LIST.map(m => (
                    <button key={m.value} type="button" onClick={() => setMood(m.value)}
                      className={`flex-1 flex flex-col items-center py-2 rounded-lg border cursor-pointer transition-all ${mood === m.value ? "border-[#00C896]/40 bg-[#00C896]/10" : "border-white/[0.05] hover:border-white/[0.1]"}`}>
                      <span className="text-base">{m.emoji}</span>
                      <span className="text-[8px] text-[#444444] mt-0.5 leading-none">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={handleCheckIn} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#00C896] text-white text-sm font-bold hover:bg-[#00DDA8] cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap">
                <i className="ri-login-box-line text-base"></i>
                {saving ? "처리 중..." : `출근 체크인 — ${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`}
              </button>
            </div>
          ) : (
            /* 출근 후 상태 */
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-[#00C896]/[0.06] border border-[#00C896]/20 rounded-xl px-4 py-3">
                <div>
                  <p className="text-[#00C896] text-xs font-semibold">출근 완료</p>
                  <p className="text-[#555555] text-[10px] mt-0.5">
                    {checkinTime?.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} 출근
                    {checkedOut ? ` · ${new Date(todayRecord!.checkout_at!).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} 퇴근` : ` · ${workHours.toFixed(1)}h 경과`}
                  </p>
                </div>
                <span className="text-xl">{MOOD_LIST.find(m => m.value === (todayRecord?.mood ?? 3))?.emoji}</span>
              </div>

              {/* 업무 일지 */}
              {(showLogInput || !todayRecord?.work_log) && !logSaved ? (
                <div className="space-y-2">
                  <label className="text-[#444444] text-[10px] flex items-center gap-1.5">
                    <i className="ri-edit-3-line text-xs"></i>오늘 업무 일지 (선택)
                  </label>
                  <textarea value={workLog} onChange={e => setWorkLog(e.target.value)} maxLength={300} rows={3}
                    placeholder="오늘 진행한 업무, 배운 점, 느낀 점을 간단히 기록하세요"
                    className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-3 py-2.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#00C896]/30 placeholder-[#2A2A2A] resize-none" />
                  <div className="flex gap-2">
                    {!checkedOut && (
                      <button type="button" onClick={handleCheckOut} disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] text-[#666666] text-xs hover:text-[#888888] cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-logout-box-line text-xs"></i>퇴근
                      </button>
                    )}
                    <button type="button" onClick={handleSaveLog} disabled={saving}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#00C896]/15 border border-[#00C896]/25 text-[#00C896] text-xs font-semibold cursor-pointer hover:bg-[#00C896]/25 transition-colors whitespace-nowrap">
                      <i className="ri-check-line text-sm"></i>{saving ? "저장 중..." : "일지 저장"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {logSaved && (
                    <div className="flex items-center gap-2 text-[#00C896] text-xs bg-[#00C896]/[0.06] border border-[#00C896]/20 rounded-lg px-3 py-2">
                      <i className="ri-check-double-line text-sm"></i>업무 일지가 저장됐어요!
                    </div>
                  )}
                  {todayRecord?.work_log && (
                    <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg px-3 py-2.5">
                      <div className="text-[#3D3D3D] text-[9px] mb-1.5 flex items-center justify-between">
                        <span>오늘 일지</span>
                        <button type="button" onClick={() => setShowLogInput(true)} className="text-[#444444] hover:text-[#777777] cursor-pointer transition-colors">
                          <i className="ri-edit-line text-[9px]"></i>
                        </button>
                      </div>
                      <p className="text-[#888888] text-xs leading-relaxed whitespace-pre-wrap">{todayRecord.work_log}</p>
                    </div>
                  )}
                  {!checkedOut && (
                    <button type="button" onClick={handleCheckOut} disabled={saving}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/[0.08] text-[#666666] text-xs hover:text-[#888888] cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-logout-box-line text-xs"></i>퇴근 기록하기
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
